import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  getUserProfile,
  getUserProfileByUsername,
  isUsernameTaken,
  changeUsername,
  updateUserProfile,
  getGameHistory,
  deleteUserAccount,
  renameGame,
} from '../services/firebase/users';
import { AVATARS, DEFAULT_AVATAR_KEY, getAvatarEmoji } from '../constants/avatars';
import Layout from '../components/Layout';
import { isProfane } from '../utils/profanity';
import { cancelSubscription } from '../services/payment';

const USERNAME_REGEX = /^[a-zA-Z0-9_åäöÅÄÖæøÆØ]{3,20}$/;

function RoundHistoryTable({ round, playerIds, playerNames }) {
  const { t } = useTranslation();
  const { scores = {}, winners = [], song, chooserId, roundNumber } = round;

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <p className="text-xs t-text-disabled mb-0.5">{t('profile.round', { n: roundNumber })}</p>
          <p className="font-semibold t-text truncate">{song?.title ?? t('profile.unknownSong')}</p>
          <p className="text-sm t-text-muted truncate">{song?.artist}</p>
        </div>
        <p className="text-xs t-text-disabled shrink-0">
          {t('profile.chosenBy', { name: playerNames[chooserId] ?? chooserId })}
        </p>
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm min-w-0">
          <caption className="sr-only">
            {t('profile.roundHistoryCaption', { n: roundNumber })}
          </caption>
          <thead>
            <tr className="text-xs t-text-disabled t-border border-b">
              <th className="text-left pb-2 pl-1 font-medium">{t('profile.playerTableHeader')}</th>
              <th className="text-center pb-2 px-3 font-medium">{t('profile.scoreTableHeader')}</th>
              <th className="text-left pb-2 px-3 font-medium">{t('profile.predictionsTableHeader')}</th>
              <th className="text-right pb-2 pr-1 font-medium">{t('profile.errorTableHeader')}</th>
            </tr>
          </thead>
          <tbody>
            {playerIds.map((uid) => {
              const data = scores[uid] ?? {};
              const actualScore = data.actualScore;
              const predictions = data.predictions ?? {};
              const isWinner = winners.includes(uid);

              const predErrors = Object.entries(predictions)
                .filter(([targetUid]) => targetUid !== uid)
                .map(([targetUid, pred]) => {
                  const actual = scores[targetUid]?.actualScore;
                  return actual !== undefined ? Math.abs(pred - actual) : null;
                })
                .filter((e) => e !== null);
              const avgErr =
                predErrors.length > 0
                  ? `${Math.round((1 - (predErrors.reduce((a, b) => a + b, 0) / predErrors.length) / 10) * 100)}%`
                  : '-';

              const predText = Object.entries(predictions)
                .filter(([targetUid]) => targetUid !== uid)
                .map(([targetUid, pred]) => `${playerNames[targetUid] ?? targetUid}: ${pred}`)
                .join(', ');

              return (
                <tr
                  key={uid}
                  className={`t-border border-b last:border-0 ${isWinner ? 't-text-highlight' : 't-text'}`}
                >
                  <td className="py-2 pl-1">
                    <span className="font-medium">{playerNames[uid] ?? uid}</span>
                    {isWinner && (
                      <span className="ml-1 text-xs" aria-label="round winner">
                        🏆
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {actualScore !== undefined ? actualScore : '-'}
                  </td>
                  <td className="py-2 px-3 t-text-muted text-xs">{predText || '-'}</td>
                  <td className="py-2 pr-1 text-right t-text-disabled text-xs">{avgErr}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function GameHistoryCard({ game, isOwnProfile, userId }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState('');

  const { playerIds = [], playerNames = {}, rounds = [], completedAt } = game;

  const date = completedAt?.toDate ? DATE_FORMAT.format(completedAt.toDate()) : 'Unknown date';
  const displayName = game.gameName || date;
  const playerList = playerIds.map((uid) => playerNames[uid] ?? uid).join(', ');

  function startEditing() {
    setNameInput(game.gameName || date);
    setEditingName(true);
    setNameSaved(false);
    setNameError('');
  }

  async function handleSaveName(e) {
    e.preventDefault();
    if (!nameInput.trim()) return;
    setSavingName(true);
    setNameError('');
    try {
      await renameGame(userId, game.id, nameInput.trim());
      // Optimistically update local display — parent would need to refetch for full update
      game.gameName = nameInput.trim();
      setEditingName(false);
      setNameSaved(true);
    } catch {
      setNameError(t('profile.errors.couldntSave'));
    } finally {
      setSavingName(false);
    }
  }

  return (
    <div className="t-bg-surface t-border border rounded-2xl overflow-hidden">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full px-5 py-4 flex items-center justify-between text-left t-focus-ring"
        aria-expanded={expanded}
        aria-controls={`rounds-${game.id}`}
      >
        <div className="min-w-0">
          <p className="font-semibold t-text">{displayName}</p>
          {game.gameName && <p className="text-xs t-text-disabled mt-0.5">{date}</p>}
          <p className="text-sm t-text-muted mt-0.5">
            {t('profile.rounds', { count: rounds.length })} · {t('profile.players', { count: playerIds.length })}
          </p>
          <p className="text-xs t-text-disabled mt-0.5 truncate">{playerList}</p>
        </div>
        <span className="t-text-disabled ml-4 text-xs" aria-hidden="true">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Rename section — only visible to the profile owner */}
      {isOwnProfile && (
        <div className="px-5 pb-3 t-border border-t">
          {editingName ? (
            <form onSubmit={handleSaveName} className="flex gap-2 pt-3">
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                maxLength={60}
                autoFocus
                aria-label={t('profile.gameNameLabel')}
                className="flex-1 t-bg-input t-border-input border rounded-lg px-3 py-1.5 t-text text-sm t-placeholder t-focus-input"
              />
              <button
                type="submit"
                disabled={savingName || !nameInput.trim()}
                className="t-btn-primary disabled:opacity-50 font-semibold rounded-lg px-3 py-1.5 text-xs transition-colors t-focus-ring"
              >
                {savingName ? t('profile.saving') : t('profile.save')}
              </button>
              <button
                type="button"
                onClick={() => { setEditingName(false); setNameError(''); }}
                className="t-text-disabled hover:t-text-muted text-xs px-2 transition-colors t-focus-ring rounded"
              >
                {t('profile.cancel')}
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={startEditing}
              className="mt-2 text-xs t-text-disabled hover:t-text-muted underline transition-colors t-focus-ring rounded"
            >
              {nameSaved ? t('profile.renamedConfirm') : t('profile.rename')}
            </button>
          )}
          {nameError && <p role="alert" className="text-xs t-text-error mt-1">{nameError}</p>}
        </div>
      )}

      {expanded && (
        <div id={`rounds-${game.id}`} className="t-border border-t divide-y divide-(--c-border)">
          {rounds.map((round) => (
            <RoundHistoryTable
              key={round.roundId}
              round={round}
              playerIds={playerIds}
              playerNames={playerNames}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, profile: ownProfile, refreshProfile, logout } = useAuth();

  const [profile, setProfile] = useState(null);
  const [bestFriendProfile, setBestFriendProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);

  const [gameHistory, setGameHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const deleteBtnRef = useRef(null);
  const cancelSubBtnRef = useRef(null);
  useEffect(() => { if (showDeleteConfirm) deleteBtnRef.current?.focus(); }, [showDeleteConfirm]);
  useEffect(() => { if (showCancelConfirm) cancelSubBtnRef.current?.focus(); }, [showCancelConfirm]);

  const isOwnProfile = user && ownProfile?.usernameLower === username.toLowerCase();
  const canSeeDetails = isOwnProfile || (profile?.profileVisible ?? false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setNotFound(false);
      setBestFriendProfile(null);
      const p = await getUserProfileByUsername(username);
      if (cancelled) return;
      if (!p) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setProfile(p);
      if (p.profileVisible || isOwnProfile) {
        if (p.stats?.bestFriend) {
          const bf = await getUserProfile(p.stats.bestFriend);
          if (!cancelled) setBestFriendProfile(bf);
        }
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  useEffect(() => {
    if (!profile || !canSeeDetails) return;
    let cancelled = false;
    async function loadHistory() {
      setHistoryLoading(true);
      const h = await getGameHistory(profile.id);
      if (!cancelled) {
        setGameHistory(h);
        setHistoryLoading(false);
      }
    }
    loadHistory();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, canSeeDetails]);

  async function handleAvatarSelect(avatarKey) {
    if (!isOwnProfile) return;
    setProfile((p) => ({ ...p, avatarKey }));
    await updateUserProfile(user.uid, { avatarKey });
    await refreshProfile();
  }

  async function handleToggleVisibility() {
    if (!isOwnProfile) return;
    const newVal = !(profile.profileVisible ?? false);
    setProfile((p) => ({ ...p, profileVisible: newVal }));
    await updateUserProfile(user.uid, { profileVisible: newVal });
  }

  async function handleUsernameChange(e) {
    e.preventDefault();
    setUsernameError('');
    if (!USERNAME_REGEX.test(newUsername)) {
      setUsernameError(t('profile.errors.invalidFormat'));
      return;
    }

    if (isProfane(newUsername)) {
      setUsernameError(t('profile.errors.profanity'));
      return;
    }
    if (newUsername === profile.username) {
      setEditingUsername(false);
      return;
    }
    setSavingUsername(true);
    try {
      if (newUsername.toLowerCase() !== profile.usernameLower) {
        const taken = await isUsernameTaken(newUsername);
        if (taken) {
          setUsernameError(t('profile.errors.taken'));
          return;
        }
      }
      await changeUsername(user.uid, newUsername);
      await refreshProfile();
      navigate(`/profile/${newUsername}`, { replace: true });
    } catch {
      setUsernameError(t('profile.errors.generic'));
    } finally {
      setSavingUsername(false);
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteUserAccount();
      await logout();
      navigate('/login', { replace: true });
    } catch {
      setDeleteError(t('profile.errors.generic'));
      setDeleting(false);
    }
  }

  async function handleCancelSubscription() {
    setCancelling(true);
    setCancelError('');
    try {
      await cancelSubscription();
      await refreshProfile();
      setShowCancelConfirm(false);
    } catch (err) {
      setCancelError(err.message || t('profile.errors.generic'));
    } finally {
      setCancelling(false);
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 t-border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-4">
            <p className="t-text-muted">{t('profile.notFound')}</p>
            <Link to="/lobby" className="inline-block t-text-accent hover:opacity-80 transition-colors t-focus-ring rounded">
              {t('profile.backToLobby')}
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const currentAvatarKey = profile.avatarKey ?? DEFAULT_AVATAR_KEY;

  return (
    <Layout>
      <div className="flex-1 flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-lg space-y-5">

          {/* Profile header */}
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-full t-bg-input t-border border flex items-center justify-center text-3xl shrink-0"
              aria-hidden="true"
            >
              {canSeeDetails ? getAvatarEmoji(currentAvatarKey) : '🔒'}
            </div>
            <div>
              <h1 className="text-2xl font-bold t-text">{profile.username}</h1>
              {isOwnProfile && !editingUsername && (
                <button
                  onClick={() => { setNewUsername(profile.username); setEditingUsername(true); }}
                  className="text-sm t-text-accent hover:opacity-80 transition-colors t-focus-ring rounded"
                >
                  {t('profile.changeUsername')}
                </button>
              )}
            </div>
          </div>

          {/* Username change form */}
          {isOwnProfile && editingUsername && (
            <form
              onSubmit={handleUsernameChange}
              className="t-bg-surface t-border border rounded-2xl p-5 space-y-4"
            >
              <h2 className="text-sm font-semibold t-text-muted">{t('profile.changeUsername')}</h2>
              <div>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  maxLength={20}
                  autoComplete="off"
                  aria-label={t('profile.newUsernameLabel')}
                  className="w-full t-bg-input t-border-input border rounded-lg px-4 py-3 t-text t-placeholder t-focus-input"
                />
                <p className="mt-1.5 text-xs t-text-disabled">
                  {t('profile.usernameHint')}
                </p>
              </div>
              {usernameError && (
                <p role="alert" className="text-sm t-text-error">{usernameError}</p>
              )}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={savingUsername || !newUsername}
                  className="flex-1 t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-2.5 transition-colors t-focus-ring"
                >
                  {savingUsername ? t('profile.saving') : t('profile.save')}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingUsername(false); setUsernameError(''); }}
                  className="flex-1 t-bg-input hover:opacity-80 t-text font-semibold rounded-lg px-4 py-2.5 transition-colors t-focus-ring"
                >
                  {t('profile.cancel')}
                </button>
              </div>
            </form>
          )}

          {/* Avatar picker (own profile only) */}
          {isOwnProfile && (
            <div className="t-bg-surface t-border border rounded-2xl p-5">
              <h2 className="text-sm font-semibold t-text-muted mb-3">{t('profile.chooseAvatar')}</h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" role="radiogroup" aria-label={t('profile.chooseAvatar')}>
                {AVATARS.map(({ key, emoji, label }) => (
                  <button
                    key={key}
                    onClick={() => handleAvatarSelect(key)}
                    role="radio"
                    aria-checked={currentAvatarKey === key}
                    aria-label={label}
                    className={`
                      aspect-square rounded-xl flex items-center justify-center text-2xl
                      transition-colors t-focus-ring
                      ${currentAvatarKey === key
                        ? 't-btn-primary ring-2 ring-current'
                        : 't-bg-input hover:opacity-80'}
                    `}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <p className="text-xs t-text-disabled mt-3">
                {t('profile.avatarPlaceholder')}
              </p>
            </div>
          )}

          {/* Visibility toggle (own profile only) */}
          {isOwnProfile && (
            <div className="t-bg-surface t-border border rounded-2xl px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium t-text">
                    {profile.profileVisible ? t('profile.publicProfile') : t('profile.privateProfile')}
                  </p>
                  <p className="text-xs t-text-disabled mt-0.5">
                    {profile.profileVisible
                      ? t('profile.publicProfileDesc')
                      : t('profile.privateProfileDesc')}
                  </p>
                </div>
                <button
                  onClick={handleToggleVisibility}
                  className="shrink-0 text-sm t-bg-input hover:opacity-80 t-text px-3 py-1.5 rounded-lg transition-colors t-focus-ring"
                >
                  {profile.profileVisible ? t('profile.makePrivate') : t('profile.makePublic')}
                </button>
              </div>
            </div>
          )}

          {/* Subscription management (own profile, monthly subscribers only) */}
          {isOwnProfile && ownProfile?.subscription?.type === 'monthly' && ownProfile?.subscription?.status === 'active' && (() => {
            const sub = ownProfile.subscription;
            const cancelAt = sub.cancelAt?.toDate
              ? sub.cancelAt.toDate()
              : sub.cancelAt
                ? new Date(sub.cancelAt)
                : null;
            const cancelAtFormatted = cancelAt
              ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(cancelAt)
              : null;
            return (
              <div className="t-bg-surface t-border border rounded-2xl p-5">
                <h2 className="text-sm font-semibold t-text-muted mb-3">{t('profile.subscription')}</h2>
                {sub.cancelAtPeriodEnd ? (
                  <div className="space-y-2">
                    <p className="text-sm t-text font-medium">{t('profile.monthlyPremium')}</p>
                    <p className="text-xs t-text-warning">
                      {cancelAtFormatted ? t('profile.cancelsOn', { date: cancelAtFormatted }) : t('profile.cancelsAtPeriodEnd')}
                    </p>
                  </div>
                ) : !showCancelConfirm ? (
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm t-text font-medium">{t('profile.monthlyPremium')}</p>
                      <p className="text-xs t-text-disabled mt-0.5">{t('profile.renewsMonthly')}</p>
                    </div>
                    <button
                      onClick={() => { setShowCancelConfirm(true); setCancelError(''); }}
                      className="shrink-0 text-sm t-bg-input hover:opacity-80 t-text px-3 py-1.5 rounded-lg transition-colors t-focus-ring"
                    >
                      {t('profile.cancelSubscription')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm t-text">
                      {t('profile.cancelConfirm')}
                    </p>
                    {cancelError && (
                      <p role="alert" className="text-sm t-text-error">{cancelError}</p>
                    )}
                    <div className="flex gap-3">
                      <button
                        ref={cancelSubBtnRef}
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        className="flex-1 t-bg-input hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed t-text font-semibold rounded-lg px-4 py-2.5 transition-colors text-sm t-focus-ring"
                      >
                        {cancelling ? t('profile.cancelling') : t('profile.yesCancelSubscription')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowCancelConfirm(false); setCancelError(''); }}
                        disabled={cancelling}
                        className="flex-1 t-bg-input hover:opacity-80 disabled:opacity-50 t-text font-semibold rounded-lg px-4 py-2.5 transition-colors text-sm t-focus-ring"
                      >
                        {t('profile.keepSubscription')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Private profile message (other player's hidden profile) */}
          {!canSeeDetails && (
            <div className="t-bg-surface t-border border rounded-2xl px-5 py-6 text-center">
              <p className="t-text-muted">{t('profile.privateMessage')}</p>
            </div>
          )}

          {/* Stats */}
          {canSeeDetails && (
            <div className="t-bg-surface t-border border rounded-2xl p-5">
              <h2 className="text-sm font-semibold t-text-muted mb-4">{t('profile.stats')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-3xl font-bold t-text">
                    {profile.stats?.gamesPlayed ?? 0}
                  </p>
                  <p className="text-sm t-text-muted">{t('profile.gamesPlayed')}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold t-text">
                    {profile.stats?.roundsWon ?? 0}
                  </p>
                  <p className="text-sm t-text-muted">{t('profile.roundsWon')}</p>
                </div>
              </div>
              {profile.miniGameHighScore > 0 && (
                <div className="mt-4 pt-4 t-border border-t flex items-center gap-3">
                  <span className="text-xl shrink-0" aria-hidden="true">🎮</span>
                  <div>
                    <p className="font-bold t-text">
                      {profile.miniGameHighScore.toFixed(1)}s
                    </p>
                    <p className="text-xs t-text-disabled">{t('miniGame.highScoreDesc')}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Best friend */}
          {canSeeDetails && bestFriendProfile && (
            <div className="t-bg-surface t-border border rounded-2xl px-5 py-4 flex items-center gap-4">
              <span className="text-2xl shrink-0" aria-hidden="true">🤝</span>
              <div className="min-w-0">
                <p className="text-sm t-text-muted">{t('profile.bestFriend')}</p>
                <p className="text-xs t-text-disabled">{t('profile.bestFriendDesc')}</p>
                <Link
                  to={`/profile/${bestFriendProfile.username}`}
                  className="font-bold t-text-accent hover:opacity-80 mt-0.5 block transition-colors t-focus-ring rounded"
                >
                  {bestFriendProfile.username}
                </Link>
              </div>
            </div>
          )}

          {/* Favourite song */}
          {canSeeDetails && profile.stats?.favouriteSong && (
            <div className="t-bg-surface t-border border rounded-2xl px-5 py-4 flex items-center gap-4">
              <span className="text-2xl shrink-0" aria-hidden="true">🎵</span>
              <div className="min-w-0">
                <p className="text-sm t-text-muted">{t('profile.highestRatedSong')}</p>
                <p className="font-bold t-text mt-0.5 truncate">
                  {profile.stats.favouriteSong.title}
                </p>
                <p className="text-sm t-text-muted truncate">
                  {profile.stats.favouriteSong.artist}
                </p>
              </div>
            </div>
          )}

          {/* Game history */}
          {canSeeDetails && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold t-text-muted">{t('profile.gameHistory')}</h2>
                <Link
                  to={`/profile/${profile?.username}/stats`}
                  className="text-xs t-text-accent hover:opacity-80 transition-opacity t-focus-ring rounded"
                >
                  {t('profile.detailedStats')}
                </Link>
              </div>
              {historyLoading ? (
                <div className="flex justify-center py-6">
                  <div className="w-6 h-6 border-2 t-border-accent border-t-transparent rounded-full animate-spin" />
                </div>
              ) : gameHistory.length === 0 ? (
                <div className="t-bg-surface t-border border rounded-2xl px-5 py-6 text-center">
                  <p className="t-text-disabled">{t('profile.noGamesYet')}</p>
                </div>
              ) : (
                gameHistory.map((g) => (
                  <GameHistoryCard key={g.id} game={g} isOwnProfile={isOwnProfile} userId={profile.id} />
                ))
              )}
            </div>
          )}

          {/* Danger zone — delete account (own profile only) */}
          {isOwnProfile && (
            <div className="t-bg-surface t-border-error border rounded-2xl p-5">
              <h2 className="text-sm font-semibold t-text-error mb-3">{t('profile.dangerZone')}</h2>
              {!showDeleteConfirm ? (
                <>
                  <p className="text-xs t-text-muted mb-4">
                    {t('profile.deleteAccountDesc')}
                  </p>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="t-btn-error text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors t-focus-ring"
                  >
                    {t('profile.deleteAccount')}
                  </button>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold t-text-error">
                      {t('profile.deleteConfirmTitle')}
                    </p>
                    <ul className="list-disc list-inside text-xs t-text-muted space-y-1 pl-1">
                      <li>{t('profile.deleteItem1')}</li>
                      <li>{t('profile.deleteItem2')}</li>
                      <li>{t('profile.deleteItem3')}</li>
                    </ul>
                    <p className="text-xs t-text-disabled">
                      {t('profile.deleteNote')}
                    </p>
                  </div>
                  {deleteError && (
                    <p role="alert" className="text-sm t-text-error">{deleteError}</p>
                  )}
                  <div className="flex gap-3">
                    <button
                      ref={deleteBtnRef}
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="flex-1 t-btn-error disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-2.5 transition-colors text-sm t-focus-ring"
                    >
                      {deleting ? t('profile.deleting') : t('profile.yesPermanentlyDelete')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); }}
                      disabled={deleting}
                      className="flex-1 t-bg-input hover:opacity-80 disabled:opacity-50 t-text font-semibold rounded-lg px-4 py-2.5 transition-colors text-sm t-focus-ring"
                    >
                      {t('profile.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <Link
            to="/lobby"
            className="block w-full text-center t-bg-input hover:opacity-80 t-text font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
          >
            {t('profile.backToLobby')}
          </Link>
        </div>
      </div>
    </Layout>
  );
}
